import { zodResolver } from "@hookform/resolvers/zod";
import {
	AlertTriangle,
	CheckCircle2,
	Edit2,
	ExternalLink,
	FileSpreadsheet,
	Loader2,
	Mail,
	Plus,
	Save,
	Trash2,
	Upload,
	Users,
	X,
} from "lucide-react";
import type React from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import FieldError from "../components/FieldError";
import LoadingSpinner from "../components/LoadingSpinner";
import { Alert, AlertDescription } from "../components/ui/alert";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import {
	Card,
	CardContent,
	CardHeader,
	CardTitle,
} from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "../components/ui/select";
import { Separator } from "../components/ui/separator";
import { Textarea } from "../components/ui/textarea";
import ValidationBanner from "../components/ValidationBanner";
import { useDocumentTemplates } from "../context/DocumentTemplateContext";
import useConfirmDialog from "../hooks/useConfirmDialog";
import { type SettingsFormData, settingsSchema } from "../schemas/settings";
import {
	getNotificationRecipients,
	type NotificationRecipientsResponse,
	updateNotificationRecipients,
} from "../services/api/configApi";
import { importPartnerTracker } from "../services/api/importService";
import type { DocumentTemplate, ImportResult } from "../types";
import { isDataUrl, openOrDownloadUrl } from "../utils/documentUrl";
import { notifications } from "../utils/notifications";

// ---------------------------------------------------------------------------
// Partner Tracker Import Section
// ---------------------------------------------------------------------------

const PartnerTrackerImport: React.FC = () => {
	const importFileRef = useRef<HTMLInputElement>(null);
	const [selectedFile, setSelectedFile] = useState<File | null>(null);
	const [importing, setImporting] = useState(false);
	const [previewResult, setPreviewResult] = useState<ImportResult | null>(null);
	const [importResult, setImportResult] = useState<ImportResult | null>(null);
	const [importError, setImportError] = useState<string | null>(null);

	const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
		const file = e.target.files?.[0];
		if (!file) return;
		setSelectedFile(file);
		setPreviewResult(null);
		setImportResult(null);
		setImportError(null);
	};

	const handlePreview = async () => {
		if (!selectedFile) return;
		setImporting(true);
		setImportError(null);
		try {
			const result = await importPartnerTracker(selectedFile, true);
			setPreviewResult(result);
		} catch (err) {
			setImportError(err instanceof Error ? err.message : "Preview failed");
		} finally {
			setImporting(false);
		}
	};

	const handleConfirmImport = async () => {
		if (!selectedFile) return;
		setImporting(true);
		setImportError(null);
		try {
			const result = await importPartnerTracker(selectedFile, false);
			setImportResult(result);
			setPreviewResult(null);
			const parts = [];
			if (result.updated > 0) parts.push(`${result.updated} updated`);
			if (result.created > 0) parts.push(`${result.created} created`);
			notifications.success(`Import complete: ${parts.join(", ")} wallet(s)`);
		} catch (err) {
			setImportError(err instanceof Error ? err.message : "Import failed");
		} finally {
			setImporting(false);
		}
	};

	const handleReset = () => {
		setSelectedFile(null);
		setPreviewResult(null);
		setImportResult(null);
		setImportError(null);
		if (importFileRef.current) importFileRef.current.value = "";
	};

	const displayResult = importResult || previewResult;
	const isPreview = !!previewResult && !importResult;

	return (
		<Card className="mb-8">
			<CardHeader className="pb-4">
				<div className="flex items-center gap-2">
					<FileSpreadsheet size={20} className="text-(--color-primary-500)" />
					<CardTitle>Partner Tracker Import</CardTitle>
				</div>
				<p className="text-(--color-muted-foreground) text-sm">
					Upload the Partner Tracker Excel file to sync partner data (customer
					success, marketing grants, tasks) with wallets.
				</p>
				<Separator />
			</CardHeader>
			<CardContent className="space-y-4">
				{/* File selection */}
				<div className="flex items-center gap-3">
					<input
						ref={importFileRef}
						type="file"
						accept=".xlsx,.xls"
						onChange={handleFileSelect}
						className="hidden"
					/>
					<Button
						type="button"
						variant="outline"
						onClick={() => importFileRef.current?.click()}
						disabled={importing}
					>
						<Upload size={16} />
						{selectedFile ? "Change File" : "Choose Excel File"}
					</Button>
					{selectedFile && (
						<span className="text-(--color-muted-foreground) text-sm">
							{selectedFile.name} ({(selectedFile.size / 1024).toFixed(0)} KB)
						</span>
					)}
				</div>

				{/* Action buttons */}
				{selectedFile && !importResult && (
					<div className="flex gap-3">
						<Button
							onClick={handlePreview}
							disabled={importing}
							variant="outline"
						>
							{importing && !previewResult ? (
								<Loader2 size={16} className="animate-spin" />
							) : (
								<FileSpreadsheet size={16} />
							)}
							{importing && !previewResult
								? "Parsing & Matching..."
								: "Preview Import"}
						</Button>
						{previewResult && previewResult.details.length > 0 && (
							<Button onClick={handleConfirmImport} disabled={importing}>
								{importing ? (
									<Loader2 size={16} className="animate-spin" />
								) : (
									<CheckCircle2 size={16} />
								)}
								{importing ? "Importing..." : "Confirm Import"}
							</Button>
						)}
					</div>
				)}

				{/* Error */}
				{importError && (
					<Alert variant="destructive">
						<AlertDescription>{importError}</AlertDescription>
					</Alert>
				)}

				{/* Results */}
				{displayResult && (
					<div className="space-y-3 rounded-(--radius-md) border border-(--color-border) p-4">
						<div className="flex items-center gap-2">
							{isPreview ? (
								<AlertTriangle size={16} className="text-amber-500" />
							) : (
								<CheckCircle2 size={16} className="text-green-500" />
							)}
							<span className="font-semibold text-sm">
								{isPreview
									? "Preview — no changes made yet"
									: "Import Complete"}
							</span>
						</div>

						{/* Stats */}
						<div className="grid grid-cols-5 gap-3">
							<div className="rounded-(--radius-sm) bg-(--color-muted) p-3 text-center">
								<div className="font-bold text-lg">
									{displayResult.totalPartners}
								</div>
								<div className="text-(--color-muted-foreground) text-xs">
									In File
								</div>
							</div>
							<div className="rounded-(--radius-sm) bg-(--color-muted) p-3 text-center">
								<div className="font-bold text-lg">{displayResult.matched}</div>
								<div className="text-(--color-muted-foreground) text-xs">
									Matched
								</div>
							</div>
							<div className="rounded-(--radius-sm) bg-(--color-muted) p-3 text-center">
								<div className="font-bold text-lg">
									{isPreview
										? displayResult.details.filter((d) => d.action === "update")
												.length
										: displayResult.updated}
								</div>
								<div className="text-(--color-muted-foreground) text-xs">
									{isPreview ? "Would Update" : "Updated"}
								</div>
							</div>
							<div className="rounded-(--radius-sm) bg-(--color-muted) p-3 text-center">
								<div className="font-bold text-green-500 text-lg">
									{displayResult.created}
								</div>
								<div className="text-(--color-muted-foreground) text-xs">
									{isPreview ? "Would Create" : "Created"}
								</div>
							</div>
							{displayResult.unmatched.length > 0 && (
								<div className="rounded-(--radius-sm) bg-(--color-muted) p-3 text-center">
									<div className="font-bold text-lg text-red-500">
										{displayResult.unmatched.length}
									</div>
									<div className="text-(--color-muted-foreground) text-xs">
										Errors
									</div>
								</div>
							)}
						</div>

						{/* Details */}
						{displayResult.details.length > 0 && (
							<details className="text-sm" open>
								<summary className="cursor-pointer font-medium text-(--color-foreground)">
									{isPreview ? "Changes to apply" : "Processed wallets"} (
									{displayResult.details.length})
								</summary>
								<div className="mt-2 max-h-48 overflow-y-auto rounded-(--radius-sm) bg-(--color-muted) p-2">
									{displayResult.details.map((d, i) => (
										<div
											key={`${d.walletId}-${i}`}
											className="flex items-center justify-between py-1 text-xs"
										>
											<span>
												<span
													className={`mr-2 inline-block rounded px-1.5 py-0.5 font-semibold text-[10px] uppercase ${
														d.action === "create"
															? "bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300"
															: "bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300"
													}`}
												>
													{d.action}
												</span>
												<strong>{d.partnerName}</strong>
												{d.action === "update" && (
													<span className="text-(--color-muted-foreground)">
														{" "}
														→ {d.walletName}
													</span>
												)}
											</span>
											<span className="text-(--color-muted-foreground)">
												{d.fields.join(", ")}
											</span>
										</div>
									))}
								</div>
							</details>
						)}

						{/* Errors (failed creates) */}
						{displayResult.unmatched.length > 0 && (
							<details className="text-sm" open>
								<summary className="cursor-pointer font-medium text-red-500">
									Failed imports ({displayResult.unmatched.length})
								</summary>
								<div className="mt-2 max-h-36 overflow-y-auto rounded-(--radius-sm) bg-(--color-muted) p-2 text-xs">
									{displayResult.unmatched.map((name) => (
										<div key={name} className="py-0.5">
											{name}
										</div>
									))}
								</div>
							</details>
						)}

						{/* Reset */}
						{importResult && (
							<Button variant="outline" size="sm" onClick={handleReset}>
								Import Another File
							</Button>
						)}
					</div>
				)}
			</CardContent>
		</Card>
	);
};

// ---------------------------------------------------------------------------
// Email Notification Recipients Section
// ---------------------------------------------------------------------------

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const NotificationSettings: React.FC = () => {
	const [loading, setLoading] = useState(true);
	const [saving, setSaving] = useState(false);
	const [applicationRecipients, setApplicationRecipients] = useState<string[]>(
		[],
	);
	const [kybRecipients, setKybRecipients] = useState<string[]>([]);
	const [adminUsers, setAdminUsers] = useState<
		NotificationRecipientsResponse["admin_users"]
	>([]);
	const [appInput, setAppInput] = useState("");
	const [kybInput, setKybInput] = useState("");
	const [appError, setAppError] = useState<string | null>(null);
	const [kybError, setKybError] = useState<string | null>(null);

	const fetchRecipients = useCallback(async () => {
		setLoading(true);
		try {
			const data = await getNotificationRecipients();
			setApplicationRecipients(data.application_recipients);
			setKybRecipients(data.kyb_recipients);
			setAdminUsers(data.admin_users);
		} catch {
			notifications.error("Failed to load notification recipients");
		} finally {
			setLoading(false);
		}
	}, []);

	useEffect(() => {
		fetchRecipients();
	}, [fetchRecipients]);

	const addRecipient = (type: "application" | "kyb", email?: string) => {
		const isApp = type === "application";
		const inputValue = email ?? (isApp ? appInput : kybInput).trim();
		const setError = isApp ? setAppError : setKybError;
		const recipients = isApp ? applicationRecipients : kybRecipients;
		const setRecipients = isApp ? setApplicationRecipients : setKybRecipients;
		const setInput = isApp ? setAppInput : setKybInput;

		if (!inputValue) return;

		if (!EMAIL_REGEX.test(inputValue)) {
			setError("Please enter a valid email address");
			return;
		}

		if (recipients.includes(inputValue)) {
			setError("This email is already in the list");
			return;
		}

		setRecipients([...recipients, inputValue]);
		setInput("");
		setError(null);
	};

	const removeRecipient = (type: "application" | "kyb", email: string) => {
		if (type === "application") {
			setApplicationRecipients((prev) => prev.filter((e) => e !== email));
		} else {
			setKybRecipients((prev) => prev.filter((e) => e !== email));
		}
	};

	const handleSave = async () => {
		setSaving(true);
		try {
			await updateNotificationRecipients({
				application_recipients: applicationRecipients,
				kyb_recipients: kybRecipients,
			});
			notifications.success("Notification recipients updated");
		} catch {
			notifications.error("Failed to update notification recipients");
		} finally {
			setSaving(false);
		}
	};

	const handleKeyDown = (
		e: React.KeyboardEvent<HTMLInputElement>,
		type: "application" | "kyb",
	) => {
		if (e.key === "Enter") {
			e.preventDefault();
			addRecipient(type);
		}
	};

	if (loading) {
		return (
			<Card className="mb-8">
				<CardContent className="flex items-center justify-center py-12">
					<Loader2
						size={24}
						className="animate-spin text-(--color-muted-foreground)"
					/>
					<span className="ml-2 text-(--color-muted-foreground) text-sm">
						Loading notification settings...
					</span>
				</CardContent>
			</Card>
		);
	}

	const renderRecipientCard = (
		type: "application" | "kyb",
		title: string,
		description: string,
		recipients: string[],
		inputValue: string,
		setInput: (v: string) => void,
		error: string | null,
		setError: (v: string | null) => void,
	) => (
		<Card>
			<CardHeader className="pb-3">
				<div className="flex items-center gap-2">
					<Mail size={18} className="text-(--color-primary-500)" />
					<CardTitle className="text-base">{title}</CardTitle>
				</div>
				<p className="text-(--color-muted-foreground) text-sm">{description}</p>
			</CardHeader>
			<CardContent className="space-y-4">
				{/* Current recipients */}
				<div className="flex flex-wrap gap-2">
					{recipients.length === 0 && (
						<span className="text-(--color-muted-foreground) text-sm italic">
							No recipients configured
						</span>
					)}
					{recipients.map((email) => (
						<Badge
							key={email}
							variant="default"
							className="flex items-center gap-1 py-1 pl-3 pr-1"
						>
							{email}
							<button
								type="button"
								onClick={() => removeRecipient(type, email)}
								className="ml-1 rounded-full p-0.5 hover:bg-white/20"
								aria-label={`Remove ${email}`}
							>
								<X size={12} />
							</button>
						</Badge>
					))}
				</div>

				{/* Add input row */}
				<div className="flex items-start gap-2">
					<div className="flex-1 space-y-1">
						<Input
							type="email"
							placeholder="email@example.com"
							value={inputValue}
							onChange={(e) => {
								setInput(e.target.value);
								setError(null);
							}}
							onKeyDown={(e) => handleKeyDown(e, type)}
							className={error ? "border-(--color-error-500)" : undefined}
						/>
						{error && (
							<p className="text-(--color-error-500) text-xs">{error}</p>
						)}
					</div>
					<Button
						type="button"
						variant="outline"
						size="sm"
						onClick={() => addRecipient(type)}
						className="mt-0.5"
					>
						<Plus size={14} />
						Add
					</Button>
				</div>

				{/* Admin suggestions */}
				{adminUsers.length > 0 && (
					<div className="space-y-2">
						<Label className="flex items-center gap-1.5 text-(--color-muted-foreground) text-xs">
							<Users size={12} />
							Suggestions
						</Label>
						<div className="flex flex-wrap gap-1.5">
							{adminUsers
								.filter((u) => !recipients.includes(u.email))
								.map((user) => (
									<Badge
										key={user.email}
										variant="secondary"
										className="cursor-pointer py-1 transition-colors hover:bg-(--color-neutral-300)"
										onClick={() => addRecipient(type, user.email)}
									>
										{user.name || user.email}
									</Badge>
								))}
							{adminUsers.every((u) => recipients.includes(u.email)) && (
								<span className="text-(--color-muted-foreground) text-xs italic">
									All admin users added
								</span>
							)}
						</div>
					</div>
				)}
			</CardContent>
		</Card>
	);

	return (
		<div className="mb-8">
			<div className="mb-4">
				<h2 className="mb-1 font-semibold text-(--color-foreground) text-lg uppercase tracking-wider">
					Email Notifications
				</h2>
				<p className="text-(--color-muted-foreground) text-sm">
					Manage who receives email notifications for applications and KYB
					submissions
				</p>
				<Separator className="mt-3" />
			</div>

			<div className="grid grid-cols-1 gap-5 md:grid-cols-2">
				{renderRecipientCard(
					"application",
					"Application Notifications",
					"Recipients notified when a new wallet application is submitted",
					applicationRecipients,
					appInput,
					setAppInput,
					appError,
					setAppError,
				)}
				{renderRecipientCard(
					"kyb",
					"KYB Notifications",
					"Recipients notified when a KYB application is submitted",
					kybRecipients,
					kybInput,
					setKybInput,
					kybError,
					setKybError,
				)}
			</div>

			<div className="mt-4 flex justify-end">
				<Button onClick={handleSave} disabled={saving}>
					{saving ? (
						<Loader2 size={16} className="animate-spin" />
					) : (
						<Save size={16} />
					)}
					{saving ? "Saving..." : "Save Changes"}
				</Button>
			</div>
		</div>
	);
};

// ---------------------------------------------------------------------------
// Settings Page
// ---------------------------------------------------------------------------

const Settings: React.FC = () => {
	const {
		templates,
		addTemplate,
		updateTemplate,
		deleteTemplate,
		loading: templatesLoading,
		error: templatesError,
	} = useDocumentTemplates();
	const { confirm } = useConfirmDialog();

	const [showAddForm, setShowAddForm] = useState(false);
	const [editingId, setEditingId] = useState<string | null>(null);
	const [uploadMode, setUploadMode] = useState<"url" | "upload">("url");
	const fileInputRef = useRef<HTMLInputElement>(null);
	const bannerRef = useRef<HTMLDivElement>(null);

	const form = useForm<SettingsFormData>({
		resolver: zodResolver(settingsSchema),
		defaultValues: {
			name: "",
			description: "",
			url: "",
			checklistItemId: "",
		},
		mode: "onSubmit",
	});

	const availableChecklistItems = [
		{ id: "sf-opportunity", label: "Salesforce Opportunity" },
		{ id: "nda", label: "NDA" },
		{ id: "consent", label: "Consent Form" },
		{ id: "kyc", label: "KYC Form" },
		{ id: "contract", label: "Contract" },
		{ id: "salesforce-case", label: "Salesforce Case" },
		{ id: "whitelist-guide", label: "Wallet Whitelisting Guide" },
		{ id: "test-cases", label: "Test Cases Documentation" },
	];

	const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
		const file = e.target.files?.[0];
		if (file) {
			const reader = new FileReader();
			reader.onloadend = () => {
				form.setValue("url", reader.result as string);
			};
			reader.readAsDataURL(file);
		}
	};

	const onValidSubmit = async (data: SettingsFormData) => {
		const templateData = {
			name: data.name,
			description: data.description ?? "",
			url: data.url,
			checklistItemId: data.checklistItemId,
		};
		try {
			if (editingId) {
				await updateTemplate(editingId, templateData);
				setEditingId(null);
			} else {
				await addTemplate(templateData);
			}
			form.reset({ name: "", description: "", url: "", checklistItemId: "" });
			setShowAddForm(false);
			setUploadMode("url");
		} catch (err) {
			form.setError("root", {
				message:
					err instanceof Error
						? err.message
						: "Failed to save template. Please try again.",
			});
		}
	};

	const onInvalidSubmit = () => {
		bannerRef.current?.scrollIntoView({ behavior: "smooth" });
	};

	const handleEdit = (template: DocumentTemplate) => {
		form.reset({
			name: template.name,
			description: template.description ?? "",
			url: template.url,
			checklistItemId: template.checklistItemId,
		});
		setEditingId(template.id);
		setShowAddForm(true);
		setUploadMode(template.url.startsWith("data:") ? "upload" : "url");
	};

	const handleCancel = () => {
		form.reset({ name: "", description: "", url: "", checklistItemId: "" });
		setEditingId(null);
		setShowAddForm(false);
		setUploadMode("url");
	};

	const getChecklistItemLabel = (id: string) => {
		return availableChecklistItems.find((item) => item.id === id)?.label || id;
	};

	if (templatesLoading) {
		return <LoadingSpinner size="lg" message="Loading settings..." centered />;
	}

	return (
		<div className="min-h-[calc(100vh-56px)] bg-(--color-background) px-6 pt-20 pb-8">
			<div className="mx-auto max-w-[1200px]">
				{templatesError && (
					<Alert variant="destructive" className="mb-6">
						<AlertDescription>
							<strong>Unable to load document templates:</strong>{" "}
							{templatesError}
						</AlertDescription>
					</Alert>
				)}

				<div className="mb-8 flex items-start justify-between">
					<div>
						<h1 className="mb-2 font-bold text-(--color-foreground) text-3xl">
							Settings
						</h1>
						<p className="text-(--color-muted-foreground)">
							Manage document templates and data imports
						</p>
					</div>
					{!showAddForm && (
						<Button onClick={() => setShowAddForm(true)}>
							<Plus size={16} />
							Add Template
						</Button>
					)}
				</div>

				{/* Partner Tracker Import */}
				<PartnerTrackerImport />

				{showAddForm && (
					<Card className="mb-8">
						<CardHeader className="pb-4">
							<div className="flex items-center justify-between">
								<h2 className="font-medium leading-none tracking-tight">
									{editingId ? "Edit Template" : "Add New Template"}
								</h2>
								<Button
									variant="ghost"
									size="icon"
									onClick={handleCancel}
									aria-label="Close"
								>
									<X size={20} />
								</Button>
							</div>
							<Separator />
						</CardHeader>
						<CardContent>
							<form
								onSubmit={form.handleSubmit(onValidSubmit, onInvalidSubmit)}
								className="space-y-5"
							>
								{form.formState.errors.root && (
									<div ref={bannerRef}>
										<ValidationBanner
											message={
												form.formState.errors.root.message ??
												"Please fix the errors below"
											}
											aria-live="polite"
										/>
									</div>
								)}
								{Object.keys(form.formState.errors).length > 0 &&
									!form.formState.errors.root && (
										<div ref={bannerRef}>
											<ValidationBanner
												message="Please fix the errors below"
												fields={Object.values(form.formState.errors)
													.map((e) => e?.message)
													.filter((m): m is string => Boolean(m))}
												aria-live="polite"
											/>
										</div>
									)}

								<div className="space-y-1.5">
									<Label htmlFor="checklistItemId">
										Document Type{" "}
										<span className="text-(--color-error-500)">*</span>
									</Label>
									<Controller
										control={form.control}
										name="checklistItemId"
										render={({ field }) => (
											<Select
												value={field.value}
												onValueChange={field.onChange}
											>
												<SelectTrigger
													id="checklistItemId"
													aria-invalid={!!form.formState.errors.checklistItemId}
													aria-describedby={
														form.formState.errors.checklistItemId
															? "checklistItemId-error"
															: undefined
													}
												>
													<SelectValue placeholder="Select a document type" />
												</SelectTrigger>
												<SelectContent>
													{availableChecklistItems.map((item) => (
														<SelectItem key={item.id} value={item.id}>
															{item.label}
														</SelectItem>
													))}
												</SelectContent>
											</Select>
										)}
									/>
									{form.formState.errors.checklistItemId && (
										<FieldError
											id="checklistItemId-error"
											message={
												form.formState.errors.checklistItemId.message ?? ""
											}
										/>
									)}
								</div>

								<div className="space-y-1.5">
									<Label htmlFor="name">
										Template Name{" "}
										<span className="text-(--color-error-500)">*</span>
									</Label>
									<Input
										type="text"
										id="name"
										{...form.register("name")}
										placeholder="e.g., Standard NDA Template"
										aria-invalid={!!form.formState.errors.name}
										aria-describedby={
											form.formState.errors.name ? "name-error" : undefined
										}
										className={
											form.formState.errors.name
												? "border-(--color-error-500)"
												: undefined
										}
									/>
									{form.formState.errors.name && (
										<FieldError
											id="name-error"
											message={form.formState.errors.name.message ?? ""}
										/>
									)}
								</div>

								<div className="space-y-1.5">
									<Label htmlFor="description">Description (Optional)</Label>
									<Textarea
										id="description"
										{...form.register("description")}
										placeholder="Brief description of this template"
										rows={3}
									/>
								</div>

								<div className="space-y-1.5">
									<Label>
										Document Source{" "}
										<span className="text-(--color-error-500)">*</span>
									</Label>
									<div className="flex gap-3">
										<Button
											type="button"
											variant={uploadMode === "url" ? "default" : "outline"}
											className="flex-1"
											onClick={() => setUploadMode("url")}
										>
											Link/URL
										</Button>
										<Button
											type="button"
											variant={uploadMode === "upload" ? "default" : "outline"}
											className="flex-1"
											onClick={() => setUploadMode("upload")}
										>
											Upload File
										</Button>
									</div>

									{uploadMode === "url" ? (
										<div className="space-y-1.5">
											<Input
												type="url"
												id="url"
												{...form.register("url")}
												placeholder="https://example.com/documents/template.pdf"
												aria-invalid={!!form.formState.errors.url}
												aria-describedby={
													form.formState.errors.url ? "url-error" : undefined
												}
												className={
													form.formState.errors.url
														? "border-(--color-error-500)"
														: undefined
												}
											/>
											{form.formState.errors.url && (
												<FieldError
													id="url-error"
													message={form.formState.errors.url.message ?? ""}
												/>
											)}
											<p className="text-(--color-muted-foreground) text-xs">
												💡 Tip: Include https:// at the beginning (e.g.,
												https://www.google.com)
											</p>
										</div>
									) : (
										<div className="space-y-2">
											<input
												ref={fileInputRef}
												type="file"
												accept=".pdf,.doc,.docx,.png,.jpg,.jpeg"
												onChange={handleFileUpload}
												className="hidden"
											/>
											<Button
												type="button"
												variant="outline"
												className="w-full"
												onClick={() => fileInputRef.current?.click()}
											>
												<Upload size={16} />
												{form.watch("url") && isDataUrl(form.watch("url"))
													? "Change File"
													: "Choose File"}
											</Button>
											{form.watch("url") && isDataUrl(form.watch("url")) && (
												<p className="text-(--color-muted-foreground) text-sm">
													✓ File uploaded successfully
												</p>
											)}
											{form.formState.errors.url && (
												<FieldError
													id="url-error"
													message={form.formState.errors.url.message ?? ""}
												/>
											)}
										</div>
									)}
								</div>

								<div className="flex justify-end gap-3 border-(--color-border) border-t pt-6">
									<Button
										type="button"
										variant="outline"
										onClick={handleCancel}
									>
										Cancel
									</Button>
									<Button type="submit">
										<Save size={16} />
										{editingId ? "Update Template" : "Add Template"}
									</Button>
								</div>
							</form>
						</CardContent>
					</Card>
				)}

				<div>
					<h2 className="mb-6 font-semibold text-(--color-foreground) text-2xl">
						Document Templates ({templates.length})
					</h2>

					{templates.length === 0 ? (
						<div className="rounded-(--radius-md) bg-(--color-muted) px-8 py-16 text-center text-(--color-muted-foreground)">
							No document templates yet. Add your first template to get started.
						</div>
					) : (
						<div className="grid grid-cols-[repeat(auto-fill,minmax(320px,1fr))] gap-5">
							{templates.map((template) => (
								<Card
									key={template.id}
									className="template-card p-5 transition-all hover:border-(--color-primary) hover:shadow-md"
								>
									<div className="mb-3 flex items-start justify-between">
										<div>
											<span className="mb-2 inline-block rounded-full bg-(--color-primary-50) px-2.5 py-1 font-semibold text-(--color-primary-600) text-xs uppercase tracking-wide">
												{getChecklistItemLabel(template.checklistItemId)}
											</span>
											<h3 className="font-semibold text-(--color-foreground) text-lg">
												{template.name}
											</h3>
										</div>
										<div className="flex gap-1">
											<Button
												variant="ghost"
												size="icon"
												onClick={() => handleEdit(template)}
												title="Edit template"
											>
												<Edit2 size={16} />
											</Button>
											<Button
												variant="ghost"
												size="icon"
												className="hover:bg-color-error-50 hover:text-(--color-error-600)"
												onClick={async () => {
													const ok = await confirm({
														title: "Delete Template",
														message: `Delete template "${template.name}"?`,
														confirmLabel: "Delete",
														variant: "danger",
													});
													if (!ok) return;
													try {
														await deleteTemplate(template.id);
													} catch (err) {
														notifications.error(
															err instanceof Error
																? err.message
																: "Failed to delete template",
														);
													}
												}}
												title="Delete template"
											>
												<Trash2 size={16} />
											</Button>
										</div>
									</div>

									{template.description && (
										<p className="mb-3 text-(--color-muted-foreground) text-sm leading-relaxed">
											{template.description}
										</p>
									)}

									<div className="my-4">
										<button
											type="button"
											onClick={() =>
												openOrDownloadUrl(template.url, template.name)
											}
											className="inline-flex items-center gap-1.5 rounded-(--radius-sm) px-3 py-1.5 font-medium text-(--color-primary-500) text-sm transition-colors hover:bg-(--color-primary-50)"
										>
											<ExternalLink size={14} />
											{isDataUrl(template.url)
												? "Download Document"
												: "View Document"}
										</button>
									</div>

									<div className="mt-4 border-(--color-border) border-t pt-4 text-(--color-muted-foreground) text-xs">
										Updated {new Date(template.updatedAt).toLocaleDateString()}
									</div>
								</Card>
							))}
						</div>
					)}
				</div>

				{/* Email Notifications */}
				<NotificationSettings />
			</div>
		</div>
	);
};

export default Settings;
